import axios from 'axios'
import Cookies from 'js-cookie'

const state = {
  playerRankings: {
    coins: [],
    paralleliums: [],
    businesses: [],
    level: []
  },
  fighterRankings: {
    victories: [],
    pvpVictories: [],
    pvpDefeat: []
  },
  playerPositions: {
    coins: null,
    paralleliums: null,
    businesses: null,
    level: null
  },
  fighterPositions: {
    victories: null,
    pvpVictories: null,
    pvpDefeat: null
  },
  loading: false,
  error: null,
  activeRanking: 'coins',
  showRankingsModal: false
}

const mutations = {
  SET_PLAYER_RANKINGS(state, { type, data }) {
    state.playerRankings[type] = data
  },
  SET_FIGHTER_RANKINGS(state, { type, data }) {
    state.fighterRankings[type] = data
  },
  SET_PLAYER_POSITION(state, { type, position, inTopList }) {
    state.playerPositions[type] = { position, inTopList }
  },
  SET_FIGHTER_POSITION(state, { type, position, inTopList }) {
    state.fighterPositions[type] = { position, inTopList }
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
    state.loading = false
  },
  SET_ACTIVE_RANKING(state, rankingType) {
    state.activeRanking = rankingType
  },
  TOGGLE_RANKINGS_MODAL(state, value) {
    state.showRankingsModal = value !== undefined ? value : !state.showRankingsModal
  }
}

const actions = {
  // Player Rankings
  async fetchPlayerRankingByType({ commit, rootState }, rankingType) {
    if (!rootState.auth.userId || !rootState.player.playerData) {
      console.warn('Cannot fetch player ranking: no authenticated user or player data');
      commit('SET_ERROR', 'Você precisa estar logado para ver rankings');
      return null;
    }
    
    const playerId = rootState.player.playerData.id;
    if (!playerId) {
      console.warn('Player ID is missing in player data');
      commit('SET_ERROR', 'ID do jogador não encontrado');
      return null;
    }
    
    commit('SET_LOADING', true);
    try {
      console.log(`Fetching ${rankingType} player ranking for player ID: ${playerId}`);
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/api/rankings/player-position`,
        {
          params: {
            playerId,
            rankingType,
            limit: 50
          },
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      
      // Handle possibility of data not being in expected format
      if (!response.data || !response.data.data) {
        console.error('Invalid response format for rankings');
        commit('SET_ERROR', 'Formato de resposta inválido do servidor');
        return null;
      }
      
      const { topEntities, playerPosition, inTopList } = response.data.data;
      
      commit('SET_PLAYER_RANKINGS', { type: rankingType, data: topEntities || [] });
      commit('SET_PLAYER_POSITION', { 
        type: rankingType, 
        position: playerPosition,
        inTopList: !!inTopList
      });
      
      return { rankings: topEntities, playerPosition, inTopList };
    } catch (error) {
      console.error(`Error fetching ${rankingType} ranking:`, error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      commit('SET_ERROR', `Falha ao carregar ranking de ${rankingType}`);
      return null;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Fighter Rankings
  async fetchFighterRankingByType({ commit, rootState }, rankingType) {
    if (!rootState.auth.userId || !rootState.player.playerData) {
      console.warn('Cannot fetch fighter ranking: no authenticated user or player data');
      commit('SET_ERROR', 'Você precisa estar logado para ver rankings');
      return null;
    }
    
    commit('SET_LOADING', true);
    try {
      // Get fighter ID from player data if it exists
      const fighter = rootState.player.playerData.fighter;
      const fighterId = fighter?.id;
      
      console.log(`Fetching ${rankingType} fighter ranking ${fighterId ? `for fighter ID: ${fighterId}` : 'without fighter ID'}`);
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/api/rankings/fighter-position`,
        {
          params: {
            fighterId,
            rankingType,
            limit: 50
          },
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      
      // Handle possibility of data not being in expected format
      if (!response.data || !response.data.data) {
        console.error('Invalid response format for fighter rankings');
        commit('SET_ERROR', 'Formato de resposta inválido do servidor');
        return null;
      }
      
      const { topEntities } = response.data.data;
      let fighterPosition, inTopList;
      
      // If we have a fighter, get their position
      if (fighterId) {
        fighterPosition = response.data.data.fighterPosition;
        inTopList = response.data.data.inTopList;
      }
      
      commit('SET_FIGHTER_RANKINGS', { type: rankingType, data: topEntities || [] });
      
      // Only set position if fighter exists
      if (fighterId) {
        commit('SET_FIGHTER_POSITION', { 
          type: rankingType, 
          position: fighterPosition,
          inTopList: !!inTopList
        });
      }
      
      return { rankings: topEntities, fighterPosition, inTopList };
    } catch (error) {
      console.error(`Error fetching fighter ${rankingType} ranking:`, error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      commit('SET_ERROR', `Falha ao carregar ranking de ${rankingType}`);
      return null;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Load all rankings
  async fetchAllRankings({ dispatch }) {
    // Player rankings
    await dispatch('fetchPlayerRankingByType', 'coins')
    await dispatch('fetchPlayerRankingByType', 'paralleliums')
    await dispatch('fetchPlayerRankingByType', 'businesses')
    await dispatch('fetchPlayerRankingByType', 'level')
    
    // Fighter rankings
    await dispatch('fetchFighterRankingByType', 'victories')
    await dispatch('fetchFighterRankingByType', 'pvpVictories')
    await dispatch('fetchFighterRankingByType', 'pvpDefeat')
  },
  
  setActiveRanking({ commit }, rankingType) {
    commit('SET_ACTIVE_RANKING', rankingType)
  },
  
  toggleRankingsModal({ commit }, value) {
    commit('TOGGLE_RANKINGS_MODAL', value)
  }
}

const getters = {
  isRankingsModalVisible: state => state.showRankingsModal,
  activeRanking: state => state.activeRanking,
  activeRankingData: state => {
    const isPlayerRanking = ['coins', 'paralleliums', 'businesses', 'level'].includes(state.activeRanking)
    
    if (isPlayerRanking) {
      return {
        rankings: state.playerRankings[state.activeRanking],
        position: state.playerPositions[state.activeRanking]
      }
    } else {
      return {
        rankings: state.fighterRankings[state.activeRanking],
        position: state.fighterPositions[state.activeRanking]
      }
    }
  },
  rankingLoading: state => state.loading
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
